import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../lib/adminService';
import { AdminNavigation } from '../../components/AdminNavigation';
import {
  TrendingUp,
  Target,
  Users,
  Activity,
  BarChart3,
  Zap,
} from 'lucide-react';

export function AdminAnalytics() {
  const [cohorts, setCohorts] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [cohortData, metricsData] = await Promise.all([
        adminService.getCohortData(),
        adminService.getPlatformMetrics(),
      ]);
      setCohorts(cohortData);
      setMetrics(metricsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="min-h-screen flex items-center justify-center ml-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </>
    );
  }

  const leanStartupMetrics = [
    {
      label: 'Activation Rate',
      value: `${metrics?.activation_rate || 0}%`,
      description: 'Users who complete key first action',
      icon: Target,
      color: 'from-orange-500 to-amber-500',
    },
    {
      label: 'Weekly Retention',
      value: `${metrics?.retention_rate_weekly || 0}%`,
      description: 'Users returning after 1 week',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
    },
    {
      label: 'Monthly Retention',
      value: `${metrics?.retention_rate_monthly || 0}%`,
      description: 'Users active after 1 month',
      icon: Activity,
      color: 'from-green-500 to-emerald-500',
    },
    {
      label: 'Avg Applications',
      value: metrics?.avg_applications_per_user?.toFixed(1) || '0',
      description: 'Per active job seeker',
      icon: BarChart3,
      color: 'from-pink-500 to-rose-500',
    },
  ];

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50 ml-64">
        <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-4xl font-bold flex items-center gap-3">
              <TrendingUp className="w-10 h-10" />
              Analytics Dashboard
            </h1>
            <p className="text-orange-100 mt-2">
              Lean Startup metrics and validated learning
            </p>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl shadow-lg p-6 text-white mb-8">
          <div className="flex items-start gap-4">
            <Zap className="w-12 h-12 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold mb-2">
                Build-Measure-Learn Cycle
              </h2>
              <p className="text-white/90 mb-4">
                Eric Ries' Lean Startup framework emphasizes rapid experimentation,
                validated learning, and iterative product development based on
                actionable metrics rather than vanity metrics.
              </p>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="font-bold mb-1">BUILD</p>
                  <p className="text-sm text-white/80">
                    Create minimal viable features
                  </p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="font-bold mb-1">MEASURE</p>
                  <p className="text-sm text-white/80">
                    Track user behavior and engagement
                  </p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="font-bold mb-1">LEARN</p>
                  <p className="text-sm text-white/80">
                    Validate or pivot based on data
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {leanStartupMetrics.map((metric) => (
            <div
              key={metric.label}
              className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200"
            >
              <div
                className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${metric.color} mb-4`}
              >
                <metric.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {metric.value}
              </div>
              <div className="text-sm font-semibold text-gray-700 mb-1">
                {metric.label}
              </div>
              <div className="text-xs text-gray-600">{metric.description}</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-orange-600" />
            Cohort Analysis
          </h2>

          {cohorts.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No cohort data available yet</p>
              <p className="text-gray-500 text-sm mt-2">
                Cohort data will be calculated as users engage with the platform
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">
                      Cohort Date
                    </th>
                    <th className="text-center py-3 px-4 font-bold text-gray-700">
                      Size
                    </th>
                    <th className="text-center py-3 px-4 font-bold text-gray-700">
                      Week 1
                    </th>
                    <th className="text-center py-3 px-4 font-bold text-gray-700">
                      Week 2
                    </th>
                    <th className="text-center py-3 px-4 font-bold text-gray-700">
                      Week 4
                    </th>
                    <th className="text-center py-3 px-4 font-bold text-gray-700">
                      Week 8
                    </th>
                    <th className="text-center py-3 px-4 font-bold text-gray-700">
                      LTV
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((cohort: any) => (
                    <tr
                      key={cohort.id}
                      className="border-b border-gray-100 hover:bg-orange-50 transition"
                    >
                      <td className="py-3 px-4 font-semibold text-gray-900">
                        {new Date(cohort.cohort_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-700">
                        {cohort.cohort_size}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-sm font-semibold">
                          {cohort.retention_week_1}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 text-sm font-semibold">
                          {cohort.retention_week_2}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 text-sm font-semibold">
                          {cohort.retention_week_4}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 rounded bg-red-100 text-red-800 text-sm font-semibold">
                          {cohort.retention_week_8}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center font-bold text-gray-900">
                        ${cohort.avg_lifetime_value?.toFixed(2) || '0.00'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
